import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {CurrencyCode, RichTextContent} from '@wix/devzai-utils-common';
import {arrayRemoveNullValues, asArray} from '@wix/devzai-utils-common';
import type {DoppeActionMediaViewSettings} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import type {DoppeHideableValue} from '../../client-server-common/types/doppe-hideable-value';
import {
    doppeHideableValueCreateHidden,
    doppeHideableValueGetVisibleValue
} from '../../client-server-common/types/doppe-hideable-value';
import {
    DoppeExternalServiceProvider,
    doppeExternalServiceProviderResolveBusinessEntityReference
} from '../../client-server-common/types/doppe-external-services';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionCtaButtonsResetSettingsForLock, DoppeLinkSpecEntityType, DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeDtoBookingService} from '../../client-server-common/types/doppe-dto-booking-service';
import {
    doppeDtoBookingServiceCapacityConvertToActionFormattedCapacity,
    doppeDtoBookingServiceLocationConvertToActionFormattedLocation,
    doppeDtoBookingServiceScheduleConvertToActionFormattedDuration,
    doppeDtoBookingServiceScheduleConvertToActionFormattedSchedule
} from '../../client-server-common/types/doppe-dto-booking-service';
import type {
    BymoPagePropsContentSettingsMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata
} from '../../client-server-common/bymo-page-props-metadata/bymo-page-props-metadata';
import type {DoppeActionPrice} from '../../client-server-common/types/doppe-action-price';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

export namespace DoppeActionTypeHoppService {

    export interface BaseActionSettings extends DoppeActionMediaViewSettings {
        servicePageLink: string;
        serviceTitle: DoppeHideableValue<string>;
        description: DoppeHideableValue<RichTextContent>;
        serviceImages: WixMediaResource[] | null;
        priceData: DoppeHideableValue<DoppeActionPrice>;
        servicePeriod: DoppeHideableValue<string>;
        serviceSchedule: DoppeHideableValue<string>;
        serviceParticipants: DoppeHideableValue<string>;
        location: DoppeHideableValue<string>;
        couponCode: DoppeHideableValue<string>;
        ribbon: DoppeHideableValue<string>;
        serviceProvider: DoppeExternalServiceProvider | null;
        serviceProviderId: string | null;
    }

    export interface ActionSettings extends
        BaseActionSettings,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
    }
}

export const doppeActionTypeHoppServiceActionContentSettingsMetadata = {
    servicePageLink: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Url, BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl),
    serviceTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
    description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
    serviceImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    priceData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
    servicePeriod: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    serviceSchedule: BymoPagePropsSpecialContentSettingMetadata.Ignored, 
    serviceParticipants: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    location: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    couponCode: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon)
} satisfies BymoPagePropsContentSettingsMetadata<DoppeActionTypeHoppService.ActionSettings>

export const doppeActionTypeHoppService = doppeActionTypeDefineMetadata<DoppeActionTypeHoppService.ActionSettings>({
    id: 'io.bymo.action/hopp-service',
    resolveActionSettings: action => {
        const { ...restSettings } = action.settings;
        return {
            servicePageLink: '',
            serviceTitle: doppeHideableValueCreateHidden(''),
            description: doppeHideableValueCreateHidden(''),
            serviceImages: null,
            priceData: doppeHideableValueCreateHidden({ 
                actualPrice: 0,
                currencyCode: 'USD'
            }),
            servicePeriod: doppeHideableValueCreateHidden(''),
            serviceSchedule: doppeHideableValueCreateHidden(''),
            serviceParticipants: doppeHideableValueCreateHidden(''),
            location: doppeHideableValueCreateHidden(''),
            couponCode: doppeHideableValueCreateHidden(''),
            ribbon: doppeHideableValueCreateHidden(''),
            serviceProvider: DoppeExternalServiceProvider.Hopp,
            serviceProviderId: null,
            ...doppeActionGetMediaViewDefaultSettingsMigration(action.settings),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...restSettings
        }
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeHoppServiceActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: { customContentSectionsTypes: {} },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                location: true,
                priceData: true,
                ribbon: true,
                serviceParticipants: true,
                servicePeriod: true,
                serviceSchedule: true,
                serviceTitle: true
            }
    },
    actionListItemSettingsMetadata: false,
    isWidgetOpener: true,
    isLinkOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    resolveUsedMedia: actionSettings => arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.serviceImages, []) ?? [])),
    resolveMainMedia: actionSettings => {
        const mediaItems = arrayRemoveNullValues(asArray(doppeHideableValueGetVisibleValue(actionSettings.serviceImages, []) ?? []));
        return mediaItems[0] ?? null;
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: actionSettings.serviceProviderId ? {
                type: DoppeLinkSpecType.Entity,
                entityType: DoppeLinkSpecEntityType.Service,
                entityId: actionSettings.serviceProviderId
            } : null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    resolveUsedBusinessEntities: actionSettings => {
        return actionSettings.serviceProvider !== null && actionSettings.serviceProviderId !== null ?
            [doppeExternalServiceProviderResolveBusinessEntityReference(actionSettings.serviceProvider, actionSettings.serviceProviderId)] : [];
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            actionSettings.servicePageLink = '';
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
});

export function doppeActionTypeHoppServiceResolveSettingsFromBoundEntity(
    actionSettings: DoppeActionTypeHoppService.BaseActionSettings,
    service: DoppeDtoBookingService
): DoppeActionTypeHoppService.BaseActionSettings {

    const durations = service.scheduale ? doppeDtoBookingServiceScheduleConvertToActionFormattedDuration({
        schedule: service.scheduale,
        serviceType: service.type
    }) : null;

    const schedule = service.scheduale ? doppeDtoBookingServiceScheduleConvertToActionFormattedSchedule({
        schedule: service.scheduale,
        serviceType: service.type,
    }) : null;

    return {
        ...actionSettings,
        ribbon: doppeHideableValueCreateHidden(''),
        serviceImages: service.media,
        servicePageLink: service.servicePageUrl ?? actionSettings?.servicePageLink ?? ``,
        priceData: service.payment ? {
            actualPrice: Number(service.payment.fixed?.price?.value ?? 0),
            currencyCode: (service.payment.fixed?.price?.currency ?? 'USD') as CurrencyCode
        } : actionSettings?.priceData ?? doppeHideableValueCreateHidden({
            actualPrice: 0,
            currencyCode: 'USD'
        }),
        serviceParticipants: service.capacity ? doppeDtoBookingServiceCapacityConvertToActionFormattedCapacity(service.capacity) : actionSettings?.serviceParticipants ?? doppeHideableValueCreateHidden(''),
        serviceSchedule: schedule ?? actionSettings?.serviceSchedule ?? doppeHideableValueCreateHidden(''),
        location: service.locations[0] ? service.locations.map(location => doppeDtoBookingServiceLocationConvertToActionFormattedLocation(location)).toString()  : actionSettings?.location ?? doppeHideableValueCreateHidden(''),
        servicePeriod: durations ? durations[0] : actionSettings?.servicePeriod ?? doppeHideableValueCreateHidden(''),
        serviceTitle: service?.name ?? actionSettings?.serviceTitle ?? '',
        description: service.description ? service.description : actionSettings?.description ?  actionSettings?.description : doppeHideableValueCreateHidden(''),
        serviceProvider: DoppeExternalServiceProvider.Hopp,
        serviceProviderId: service.id
    }

}

export function convertDoppeDtoBookingServiceToHoppServiceSettings(options: {
    service: DoppeDtoBookingService,
    serviceSettings?: DoppeActionTypeHoppService.ActionSettings
}) {
    const { service, serviceSettings = {} } = options;
    return {
        ...doppeActionContentSectionSettingsDefaultValuesMigration(serviceSettings),
        ...doppeActionCtaButtonsSettingsDefaultValuesMigration({ ctaButtons: [] }, false),
        ...serviceSettings,
        ...doppeActionGetMediaViewDefaultSettingsMigration(serviceSettings),
        servicePageLink: service.servicePageUrl ?? '',
        serviceTitle: doppeHideableValueCreateHidden(service.name ?? ''),
        description: doppeHideableValueCreateHidden(service.description ?? ''),
        serviceImages: service.media,
        priceData: service.payment ? {
            actualPrice: Number(service.payment.fixed?.price?.value ?? 0),
            currencyCode: (service.payment.fixed?.price?.currency ?? 'USD') as CurrencyCode
        } :  doppeHideableValueCreateHidden({
            actualPrice: 0,
            currencyCode: 'USD' as CurrencyCode
        }),
        servicePeriod: doppeHideableValueCreateHidden(''),
        serviceSchedule: doppeHideableValueCreateHidden(''),
        serviceParticipants: service.capacity ? doppeHideableValueCreateHidden(service.capacity.toString()) : doppeHideableValueCreateHidden(''),
        location: doppeHideableValueCreateHidden(service.locations?.[0]?.formattedAddress ?? ''),
        couponCode: doppeHideableValueCreateHidden(''),
        ribbon: doppeHideableValueCreateHidden(''),
        serviceProvider: DoppeExternalServiceProvider.Hopp,
        serviceProviderId: service.id,
    } satisfies DoppeActionTypeHoppService.ActionSettings
}